import React from 'react';
import styled from 'styled-components';
import { FaFileExport, FaSlidersH } from 'react-icons/fa';

import Button from '../../../components/Button/Button';
import useScreenQuery from '../../../hooks/useScreenQuery';
import PlayupThemeProvider from '../../../themes/PlayupThemeProvider';
import { TitleHeader } from '../sharedStyledComponents';
import { BodyContainer, FieldText, PageContainer } from '../sharedStyles';
import FixedLoader from '../../../components/Loader/FixedLoader';
import TransactionItem from './TransactionItem';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import SectionTab from '../PortalSectionTab/PortalSectionTab';
import { BrandNames } from './transactionsData';

const filterTabs = [
	{
		label: 'All',
		value: 'all',
	},
	{
		label: 'Deposits',
		value: 'deposits',
	},
	{
		label: 'Withdrawals',
		value: 'withdrawals',
	},
	{
		label: 'Wins',
		value: 'wins',
	},
];

/*
	DevNote: Components not migrated to storybook and why:
	DatePicker: Require PostCss8 for react-datepicker
*/

const PortalTransactions = ({
	isLoading,
	category,
	region,
	transactions,
	dateRange,
	exportError,
	setCategory,
	updateRange,
	downloadTransactions,
	DatePicker,
	brandName = BrandNames.PlayUp,
}: PortalTransactionsProps) => {
	const { isSmall } = useScreenQuery();
	const [showProductFilterMenu, setShowProductFilterMenu] = React.useState<boolean>(false);
	const [selectedProduct, setSelectedProduct] = React.useState<string>('all');

	return (
		<PlayupThemeProvider>
			{isSmall && <TitleHeader title={'Cash Transactions'} />}
			<TransactionsPageContainer isSmallScreen={isSmall}>
				<PageHeader isSmallScreen={isSmall}>
					<SectionTab
						setCategory={(label) => setCategory(label as 'all' | 'deposits' | 'withdrawals' | 'wins')}
						category={category}
						isSmall={isSmall}
						tabs={filterTabs}
					/>
					{!isSmall && (
						<>
							{false && (
								<FilterMenuSection>
									<FilterMenuToggler onClick={() => setShowProductFilterMenu(!showProductFilterMenu)}>
										<FilterMenuIcon $isSmallScreen={isSmall} />
									</FilterMenuToggler>
								</FilterMenuSection>
							)}
							{showProductFilterMenu && (
								<FilterMenuContainer>
									<FilterMenu>
										<FilterMenuOption selected={selectedProduct === 'all'} onClick={() => setCategory('all')}>
											<FilterOptionText isSmallScreen={isSmall}>All</FilterOptionText>
										</FilterMenuOption>
										<FilterMenuOption
											selected={selectedProduct === 'playup'}
											onClick={() => setSelectedProduct('playup')}
										>
											<FilterOptionText isSmallScreen={isSmall}>PlayUp</FilterOptionText>
										</FilterMenuOption>
										<FilterMenuOption
											selected={selectedProduct === 'draftstars'}
											onClick={() => setSelectedProduct('draftstars')}
										>
											<FilterOptionText isSmallScreen={isSmall}>Draftstars</FilterOptionText>
										</FilterMenuOption>
										{/* <FilterMenuOption
											selected={selectedProduct === 'prosper'}
											onClick={() => setSelectedProduct('prosper')}
										>
											<FilterOptionText isSmallScreen={isSmall}>Prosper</FilterOptionText>
										</FilterMenuOption> */}
									</FilterMenu>
								</FilterMenuContainer>
							)}
							<ExportSection isSmallScreen={isSmall}>
								<DatePicker
									onChange={updateRange('from')}
									label="FROM:"
									selected={dateRange.from}
									maxDate={dateRange.to}
								/>
								<DatePicker
									onChange={updateRange('to')}
									label="TO:"
									selected={dateRange.to}
									maxDate={new Date()}
									minDate={dateRange.from}
								/>
								<ExportButton onClick={downloadTransactions}>
									<ExportButtonText isSmallScreen={isSmall}>All Export</ExportButtonText>
									<ExportButtonIcon $isSmallScreen={isSmall} />
								</ExportButton>
							</ExportSection>
						</>
					)}
				</PageHeader>
				{exportError && (
					<ErrorMessageContainer>
						<ErrorMessage>{exportError}</ErrorMessage>
					</ErrorMessageContainer>
				)}
				{isLoading && (
					<BodyContainer isSmallScreen={isSmall}>
						<FixedLoader />
					</BodyContainer>
				)}
				{!isLoading && transactions.length < 1 && (
					<BodyContainer isSmallScreen={isSmall}>
						<CenteredFieldText isSmallScreen={isSmall}>
							No {category === 'all' ? '' : category} cash transactions to display
						</CenteredFieldText>
					</BodyContainer>
				)}

				{!isLoading && transactions.length > 0 && (
					<TableContainer>
						{!isSmall && (
							<TableHeader>
								<TableHeaderItem>Transaction ID#</TableHeaderItem>
								<TableHeaderItem>Date & Time</TableHeaderItem>
								<TableHeaderItem>Type</TableHeaderItem>
								<TableHeaderItem>Product</TableHeaderItem>
								<TableHeaderItem>Amount</TableHeaderItem>
								<TableHeaderItem>Balance</TableHeaderItem>
							</TableHeader>
						)}
						{transactions.map((transaction) => (
							<TransactionItem key={transaction.id} transaction={transaction} region={region} brandName={brandName} />
						))}
					</TableContainer>
				)}
			</TransactionsPageContainer>
		</PlayupThemeProvider>
	);
};

export default PortalTransactions;
export interface PortalTransactionsProps {
	isLoading: boolean;
	category: 'all' | 'deposits' | 'withdrawals' | 'wins';
	region: string;
	transactions: Array<WalletTransaction>;
	dateRange: { from: Date; to: Date };
	exportError?: string;
	setCategory: (category: 'all' | 'deposits' | 'withdrawals' | 'wins') => void;
	updateRange: (type: 'from' | 'to') => void;
	downloadTransactions: () => void;
	DatePicker: React.FC<any>;
	brandName?: string;
}

const TransactionsPageContainer = styled(PageContainer)(({ theme, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	backgroundColor: theme.colors.white,
	margin: !isSmallScreen ? '30px auto 0' : 'auto',
	maxWidth: !isSmallScreen ? '1300px' : 'auto',
}));

const PageHeader = styled.div<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: isSmallScreen ? 'column' : 'row',
	width: '100%',
	backgroundColor: theme.colors.darkR,
}));

const FilterMenuSection = styled.div({
	display: 'flex',
	flexDirection: 'row',
	height: '55px',
	minHeight: '55px',
});

const ExportSection = styled.div<{ isSmallScreen }>({
	flex: 1,
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	height: '55px',
	minHeight: '55px',
});

const FilterOption = styled.button<{ selected: boolean }>`
	flex: 1;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${({ theme, selected }) => (selected ? theme.colors.darkGreyRHighlight : theme.colors.darkGreyR)};

	&:hover {
		background-color: ${({ theme }) => theme.colors.darkGreyRHighlight};
	}

	&:focus {
		outline: none;
	}
`;

const FilterOptionText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.white,
	userSelect: 'none',
	fontSize: isSmallScreen ? '14px' : '18px',
}));

const FilterMenuToggler = styled.div({
	flex: 1,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100%',
	maxWidth: '30px',
	margin: '0 15px',
	cursor: 'pointer',
});

const FilterMenuIcon = styled(FaSlidersH)<{ $isSmallScreen: boolean }>(({ theme, $isSmallScreen }) => ({
	color: theme.colors.white,
	fontSize: $isSmallScreen ? '18px' : '22px',
}));

const FilterMenuContainer = styled.div({
	position: 'relative',
	display: 'inline-block',
});

const FilterMenu = styled.div`
	position: absolute;
	top: 55px;
	left: -50px;
	z-index: 50;
	display: flex;
	flex-direction: column;
	border-radius: 5px;
	color: ${({ theme }) => theme.colors.white};
	background-color: ${({ theme }) => theme.colors.darkR};
	box-shadow: 1px 1px 10px ${({ theme }) => theme.colors.shadow};
`;

const FilterMenuOption = styled(FilterOption)`
	padding: 15px 25px;

	&:not(:last-child) {
		border-bottom: 1px solid;
		border-color: ${({ theme }) => theme.colors.darkGreyRHighlight};
	}

	&:first-child {
		border-top-left-radius: 5px;
		border-top-right-radius: 5px;
	}

	&:last-child {
		border-bottom-left-radius: 5px;
		border-bottom-right-radius: 5px;
	}
`;

const ExportButton = styled(Button)(({ theme }) => ({
	flex: 2,
	height: '100%',
	backgroundColor: theme.colors.darkR,
	border: '1px solid',
	borderColor: theme.colors.greyR,
	borderRadius: '5px',
	maxWidth: '150px',
	marginLeft: '15px',
}));

const ExportButtonText = styled.span<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.greyR,
	fontWeight: 'normal',
	fontSize: isSmallScreen ? '14px' : '18px',
}));

const ExportButtonIcon = styled(FaFileExport)<{ $isSmallScreen: boolean }>(({ theme, $isSmallScreen }) => ({
	color: theme.colors.greyR,
	marginLeft: '5px',
	fontSize: $isSmallScreen ? '14px' : '18px',
}));

const ErrorMessageContainer = styled.div(({ theme }) => ({
	width: '100%',
	padding: '10px',
	textAlign: 'center',
	backgroundColor: theme.colors.darkR,
}));

const CenteredFieldText = styled(FieldText)({
	textAlign: 'center',
});

const TableContainer = styled.div(({ theme }) => ({
	width: '100%',
	backgroundColor: theme.colors.darkR,
}));

const TableHeader = styled.div(({ theme }) => ({
	display: 'flex',
	marginTop: '20px',
	fontSize: '16px',
	color: theme.colors.lightGreyR,
}));

const TableHeaderItem = styled.p(({ theme }) => ({
	flex: 1,
	padding: '12px 18px',
	color: theme.colors.greyR,
}));
